/**
 * PromptEditorModal Component
 * 
 * Modal for editing persona prompts with template variable support.
 * Provides a rich editing experience with variable autocomplete,
 * validation, and save/cancel/reset actions.
 */

import React, { useState, useEffect, useRef } from 'react';
import { getApiUrl } from '../../utils/apiUtils';

// Types
interface PersonaPrompt {
  id: number;
  persona_type: string;
  grade_level: string;
  prompt_text: string;
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface PromptVariable {
  variable_name: string;
  description: string;
  example_value: string;
  is_required: boolean;
}

interface PromptEditorModalProps {
  prompt: PersonaPrompt;
  onClose: () => void;
  onSave: () => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
}

export default function PromptEditorModal({ prompt, onClose, onSave, onError, onSuccess }: PromptEditorModalProps) {
  const [promptText, setPromptText] = useState(prompt.prompt_text);
  const [variables, setVariables] = useState<PromptVariable[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showVariables, setShowVariables] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    loadVariables();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompt.persona_type]);

  useEffect(() => {
    setHasChanges(promptText !== prompt.prompt_text);
  }, [promptText, prompt.prompt_text]);

  const loadVariables = async () => {
    try {
      // For now, we'll use hardcoded variables per persona
      // In production, these could be fetched from the API
      const personaVariables = getVariablesForPersona(prompt.persona_type);
      setVariables(personaVariables);
    } catch (error) {
      console.error('Error loading variables:', error);
    }
  };

  const getVariablesForPersona = (personaType: string): PromptVariable[] => {
    const commonVariables: PromptVariable[] = [
      { variable_name: 'child_age', description: "Child's age", example_value: '7', is_required: true },
      { variable_name: 'story_title', description: 'Title of the story', example_value: "Charlotte's Web", is_required: true },
      { variable_name: 'reading_progress', description: 'Reading progress percentage', example_value: '45', is_required: true },
    ];

    const specificVariables: Record<string, PromptVariable[]> = {
      gentle_teacher: [
        { variable_name: 'difficult_word', description: 'Word the child struggled with', example_value: 'magnificent', is_required: false },
        { variable_name: 'error_description', description: 'Description of reading error', example_value: 'mispronounced the word', is_required: false },
        { variable_name: 'specific_praise', description: 'Specific achievement to praise', example_value: 'reading fluently', is_required: false },
        { variable_name: 'encouragement', description: 'Encouraging message', example_value: 'Keep up the great work!', is_required: false },
        { variable_name: 'transition_message', description: 'Transition to next section', example_value: "Let's continue to the next page", is_required: false },
        { variable_name: 'practice_suggestion', description: 'Practice suggestion', example_value: 'try sounding it out slowly', is_required: false },
      ],
      pronunciation_coach: [
        { variable_name: 'difficult_word', description: 'Word to coach pronunciation on', example_value: 'magnificent', is_required: true },
        { variable_name: 'syllable_breakdown', description: 'Word broken into syllables', example_value: 'mag-nif-i-cent', is_required: false },
        { variable_name: 'phonetic_spelling', description: 'Phonetic spelling', example_value: 'mag-NIF-ih-sent', is_required: false },
        { variable_name: 'rhyming_hints', description: 'Rhyming words as hints', example_value: 'magnificent rhymes with "sent"', is_required: false },
        { variable_name: 'first_syllable', description: 'First syllable emphasis', example_value: 'mag', is_required: false },
        { variable_name: 'error_type', description: 'Type of pronunciation error', example_value: 'syllable stress', is_required: false },
        { variable_name: 'accuracy_score', description: 'Pronunciation accuracy score', example_value: '75', is_required: false },
        { variable_name: 'opening_message', description: 'Opening coaching message', example_value: "Let's work on this word together", is_required: false },
        { variable_name: 'mispronunciations', description: 'List of mispronunciations', example_value: 'mag-ni-FISH-ent', is_required: false },
      ],
      quiz_critter: [
        { variable_name: 'milestone_type', description: 'MIDPOINT or COMPLETION', example_value: 'MIDPOINT', is_required: true },
        { variable_name: 'milestone_focus', description: 'Focus of milestone questions', example_value: 'characters and plot', is_required: false },
        { variable_name: 'milestone_message', description: 'Milestone celebration message', example_value: "You're halfway through!", is_required: false },
        { variable_name: 'questions_list', description: 'List of comprehension questions', example_value: '1. What did Wilbur learn?\n2. Who helped Charlotte?', is_required: false },
        { variable_name: 'first_question', description: 'First question to ask', example_value: 'What was the main event in this chapter?', is_required: false },
        { variable_name: 'specific_praise', description: 'Specific achievement to praise', example_value: 'understanding the character motivations', is_required: false },
        { variable_name: 'transition_message', description: 'Transition after quiz', example_value: 'Great job! Back to the story!', is_required: false },
      ],
      role_ranger: [
        { variable_name: 'character_name', description: 'Name of the character to roleplay', example_value: 'Wilbur', is_required: true },
        { variable_name: 'character_traits', description: 'Comma-separated character traits', example_value: 'kind, curious, friendly', is_required: false },
        { variable_name: 'opening_line', description: "Character's opening dialogue", example_value: 'Hello! I am Wilbur the pig.', is_required: false },
        { variable_name: 'conversation_starters', description: 'Comma-separated conversation starters', example_value: 'What do you think about friendship?, Have you ever felt lonely?', is_required: false },
        { variable_name: 'story_event', description: 'Current story event context', example_value: 'Wilbur just met Charlotte', is_required: false },
      ],
    };

    return [...commonVariables, ...(specificVariables[personaType] || [])];
  };

  const handleInsertVariable = (variableName: string) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const textBefore = promptText.substring(0, start);
    const textAfter = promptText.substring(end);
    const variableText = `{${variableName}}`;
    
    const newText = textBefore + variableText + textAfter;
    setPromptText(newText);

    // Move cursor after inserted variable
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + variableText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleSave = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch(
        getApiUrl(`/v1/dashboard/persona-prompts/${prompt.persona_type}/${prompt.grade_level}`),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt_text: promptText,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to save prompt: ${response.statusText}`);
      }

      onSuccess?.('Prompt saved successfully!');
      onSave();
    } catch (error) {
      console.error('Error saving prompt:', error);
      onError?.('Failed to save prompt. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset this prompt to default? Your changes will be lost.')) {
      return;
    }

    try {
      const response = await fetch(
        getApiUrl(`/v1/dashboard/persona-prompts/${prompt.persona_type}/${prompt.grade_level}/reset`),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to reset prompt: ${response.statusText}`);
      }

      onSuccess?.('Prompt reset to default successfully!');
      onSave();
    } catch (error) {
      console.error('Error resetting prompt:', error);
      onError?.('Failed to reset prompt. Please try again.');
    }
  };

  const handleClose = () => {
    if (hasChanges && !window.confirm('You have unsaved changes. Are you sure you want to close?')) {
      return;
    }
    onClose();
  };

  const getPersonaDisplayName = (type: string): string => {
    const names: Record<string, string> = {
      gentle_teacher: 'Gentle Teacher',
      pronunciation_coach: 'Pronunciation Coach',
      quiz_critter: 'Quiz Critter',
      role_ranger: 'Role Ranger',
    };
    return names[type] || type;
  };

  const requiredVariables = variables.filter(v => v.is_required);
  const optionalVariables = variables.filter(v => !v.is_required);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">
              Edit Prompt: {getPersonaDisplayName(prompt.persona_type)}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Grade Level: {prompt.grade_level} • {hasChanges ? '✏️ Unsaved changes' : '✓ No changes'}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Editor Panel */}
          <div className="flex-1 flex flex-col p-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prompt Text
            </label>
            <textarea
              ref={textareaRef}
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
              placeholder="Enter your prompt here..."
              style={{ resize: 'none' }}
            />
            <div className="mt-2 text-xs text-gray-500">
              {promptText.length} characters • Use {'{variable_name}'} syntax for dynamic content
            </div>
          </div>

          {/* Variables Panel */}
          {showVariables && (
            <div className="w-80 bg-gray-50 border-l border-gray-200 overflow-y-auto">
              <div className="p-4 border-b border-gray-200 sticky top-0 bg-gray-50">
                <h3 className="font-semibold text-gray-800 mb-1">Template Variables</h3>
                <p className="text-xs text-gray-600">Click to insert into prompt</p>
              </div>

              {/* Required Variables */}
              {requiredVariables.length > 0 && (
                <div className="p-4 border-b border-gray-200">
                  <h4 className="text-xs font-semibold text-red-700 uppercase mb-2">
                    Required Variables
                  </h4>
                  {requiredVariables.map(variable => (
                    <button
                      key={variable.variable_name}
                      onClick={() => handleInsertVariable(variable.variable_name)}
                      className="w-full text-left px-3 py-2 mb-2 bg-white border border-red-200 rounded hover:bg-red-50 transition-colors"
                    >
                      <div className="font-mono text-sm text-red-700">
                        {'{' + variable.variable_name + '}'}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">{variable.description}</div>
                      <div className="text-xs text-gray-500 mt-1 italic">
                        Example: {variable.example_value}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Optional Variables */}
              {optionalVariables.length > 0 && (
                <div className="p-4">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">
                    Optional Variables
                  </h4>
                  {optionalVariables.map(variable => (
                    <button
                      key={variable.variable_name}
                      onClick={() => handleInsertVariable(variable.variable_name)}
                      className="w-full text-left px-3 py-2 mb-2 bg-white border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                    >
                      <div className="font-mono text-sm text-gray-700">
                        {'{' + variable.variable_name + '}'}
                      </div>
                      <div className="text-xs text-gray-600 mt-1">{variable.description}</div>
                      <div className="text-xs text-gray-500 mt-1 italic">
                        Example: {variable.example_value}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={() => setShowVariables(!showVariables)}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded transition-colors"
          >
            {showVariables ? '« Hide' : 'Show »'} Variables
          </button>

          <div className="flex gap-2">
            <button
              onClick={handleReset}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
            >
              Reset to Default
            </button>
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
